import { resolveScheduleStepLabel } from "@patient-watch/i18n";
import type { DueEntryT, EntryValueViewT, EntryViewT } from "@patient-watch/types";
import { UUIDs } from "@patient-watch/types";
import type { ChartConfig } from "../types";
import { isBmiForm } from "../bmiChart";

/** Min/max of plotted numeric values in chart data (respects stacked totals). */
export function getChartDataNumericExtents(
    chartData: Array<Record<string, string | number | undefined>>,
    dataKeys: string[],
    chartType: "line" | "stacked_area" | "bar" | "stacked_bar"
): { min: number | null; max: number | null } {
    let min: number | null = null;
    let max: number | null = null;

    for (const point of chartData) {
        if (chartType === "stacked_area" || chartType === "stacked_bar") {
            let stackTotal = 0;
            for (const key of dataKeys) {
                const value = point[key];
                if (typeof value === "number" && Number.isFinite(value)) {
                    stackTotal += value;
                }
            }
            min = min === null ? stackTotal : Math.min(min, stackTotal);
            max = max === null ? stackTotal : Math.max(max, stackTotal);
            continue;
        }

        for (const key of dataKeys) {
            const value = point[key];
            if (typeof value === "number" && Number.isFinite(value)) {
                min = min === null ? value : Math.min(min, value);
                max = max === null ? value : Math.max(max, value);
            }
        }
    }

    return { min, max };
}

/**
 * Extend the Y domain so curved lines (natural/monotone) and dot markers are not clipped
 * at the top or bottom of the plot area.
 */
export function expandYDomainForCurve(
    yMin: number,
    yMax: number,
    dataMin: number | null,
    dataMax: number | null,
    options?: { usesCurvedLines?: boolean; dotRadius?: number }
): [number, number] {
    if (dataMin == null && dataMax == null) {
        return [yMin, yMax];
    }

    const usesCurves = options?.usesCurvedLines ?? true;
    const dotRadius = options?.dotRadius ?? 5;
    const span = Math.max(yMax - yMin, 1);
    const proportionalPad = usesCurves ? Math.max(span * 0.1, 0.75) : Math.max(span * 0.04, 0.35);
    const dotPad = dotRadius <= 3 ? 0.25 : dotRadius <= 5 ? 0.5 : 1;

    let nextMin = yMin;
    let nextMax = yMax;

    if (dataMax != null) {
        nextMax = Math.max(yMax, dataMax + proportionalPad + dotPad);
    }
    if (dataMin != null) {
        nextMin = Math.min(yMin, dataMin - proportionalPad);
    }

    nextMin = Math.max(nextMin, yMin);

    if (Number.isInteger(yMin) && span <= 20) {
        nextMin = Math.floor(nextMin);
        nextMax = Math.ceil(nextMax);
    }

    return [nextMin, nextMax];
}

/** Fixed clinical Y domains that should not be padded from data extents. */
const FIXED_FORM_Y_DOMAINS: Array<{ match: (formId: string) => boolean; domain: [number, number] }> = [
    {
        match: (formId) => formId === UUIDs.forms.eq5d5l || formId === "form-eq5d5l-demo" || /eq5d/i.test(formId),
        domain: [0, 1],
    },
    {
        // VAS / NRS pain score is 0-10 in Patient Watch (not a 0-100 mm scale).
        match: (formId) => formId === "form-vas-demo" || /(^|[-_])vas($|[-_])/i.test(formId),
        domain: [0, 10],
    },
];

/** Returns a locked [min, max] for forms with a fixed clinical scale (e.g. EQ-5D index 0-1). */
export function getFixedChartYDomain(formId: string): [number, number] | null {
    return FIXED_FORM_Y_DOMAINS.find((entry) => entry.match(formId))?.domain ?? null;
}

/** Drops float noise from axis maths, e.g. 12 * 0.05 = 0.6000000000000001 becomes 0.6. */
export function roundAxisValue(value: number): number {
    return Number.isFinite(value) ? Number(value.toPrecision(12)) : value;
}

/** Axis tick label without float noise. */
export function formatAxisTick(value: number): string {
    return String(roundAxisValue(value));
}

/** A 1 / 2 / 5 x 10^k step that splits `span` into about four parts. */
function niceAxisStep(span: number): number {
    const rough = span / 4;
    const magnitude = 10 ** Math.floor(Math.log10(rough));
    const residual = rough / magnitude;
    return residual >= 5 ? 5 * magnitude : residual >= 2 ? 2 * magnitude : magnitude;
}

/** Y domain for "fit" mode: data extents snapped out to a nice step. */
export function niceFitDomain(dataMin: number, dataMax: number): [number, number] {
    if (!Number.isFinite(dataMin) || !Number.isFinite(dataMax)) return [0, 10];
    if (dataMin === dataMax) {
        const pad = Math.max(Math.abs(dataMin) * 0.1, 1);
        return [roundAxisValue(dataMin - pad), roundAxisValue(dataMax + pad)];
    }
    const step = niceAxisStep(dataMax - dataMin);
    const lo = roundAxisValue(Math.floor(roundAxisValue(dataMin / step)) * step);
    const hi = roundAxisValue(Math.ceil(roundAxisValue(dataMax / step)) * step);
    return lo === hi ? [roundAxisValue(lo - step), roundAxisValue(hi + step)] : [lo, hi];
}

/** Y-axis ticks for small integer scales (e.g. 0-4 or 0-10). */
export function buildYAxisTicks(yMin: number, yMax: number): number[] {
    const yRange = yMax - yMin;
    // Unit interval (EQ-5D index): avoid Math.round collapsing the midpoint to 1.
    if (yMin === 0 && yMax === 1) {
        return [0, 0.25, 0.5, 0.75, 1];
    }
    if (yRange <= 10 && Number.isInteger(yMin) && Number.isInteger(yMax)) {
        return Array.from({ length: yMax - yMin + 1 }, (_, i) => yMin + i);
    }
    if (yRange <= 10) {
        // Fractional bounds (fit / custom domains): evenly spaced nice ticks, not a rounded midpoint that can fall
        // outside a range like 0.25-0.6.
        const step = niceAxisStep(yRange);
        const first = Math.ceil(roundAxisValue(yMin / step)) * step;
        const ticks = [yMin];
        for (let tick = first; tick < yMax; tick += step) {
            const rounded = roundAxisValue(tick);
            if (rounded > yMin && rounded < yMax) ticks.push(rounded);
        }
        ticks.push(yMax);
        return ticks;
    }
    if (yRange <= 20) {
        return Array.from({ length: Math.floor(yRange / 5) + 1 }, (_, i) => yMin + i * 5);
    }
    if (yRange <= 50) {
        return Array.from({ length: Math.floor(yRange / 10) + 1 }, (_, i) => yMin + i * 10);
    }
    if (yRange <= 100) {
        return [
            yMin,
            yMin + Math.round(yRange / 4),
            yMin + Math.round(yRange / 2),
            yMin + Math.round((3 * yRange) / 4),
            yMax,
        ];
    }
    const stepSize = Math.ceil(yRange / 4);
    return Array.from({ length: 5 }, (_, i) => yMin + i * stepSize).filter((t) => t <= yMax);
}

/** Cap display precision to significant figures so aggregated means do not show float noise. */
export function roundChartScore(value: number, maxSignificantDigits = 3): number {
    if (!Number.isFinite(value) || value === 0) return value;
    return Number(value.toPrecision(maxSignificantDigits));
}

export function formatChartScoreDisplay(value: number, formId: string, maxScore?: number | null): string {
    const rounded = roundChartScore(value);
    if (maxScore != null && !isBmiForm(formId)) {
        return `${rounded}/${maxScore}`;
    }

    return String(rounded);
}

/** Intl format for NumberFlow / tooltip labels - max 3 significant figures. */
export const CHART_SCORE_NUMBER_FORMAT: Intl.NumberFormatOptions = {
    maximumSignificantDigits: 3,
};

export function shouldShowScoreFraction(formId: string, maxScore?: number | null): boolean {
    if (isBmiForm(formId)) return false;
    return maxScore != null;
}

const CHART_COLORS = [
    "hsl(var(--chart-1))",
    "hsl(var(--chart-2))",
    "hsl(var(--chart-3))",
    "hsl(var(--chart-4))",
    "hsl(var(--chart-5))",
];

export interface ChartSeriesMeta {
    label: string;
    chartOrder: number;
    maxScore: number | null;
    isPrimary: boolean;
    color: string;
    /**
     * Translation-stable scoring identifier (e.g. "koos:pain", "womac:summary"), when the
     * series is an engine-produced output. Prefer this over `label` as a durable series
     * key (the label is human/translatable). NULL on legacy/bespoke or raw-input series.
     */
    scoreTarget: string | null;
}

function finiteNumber(value: unknown): number | null {
    return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function inputValue<T extends "max_score" | "max_value" | "min_value">(ev: EntryValueViewT, key: T): number | null {
    const direct = finiteNumber(ev[key]);
    if (direct != null) {
        return direct;
    }

    const inputs = (ev as unknown as { inputs?: Partial<Record<T, number>> }).inputs;
    return inputs && typeof inputs === "object" ? finiteNumber(inputs[key]) : null;
}

/** Read a scoring-membership string column from the row, falling back to the nested `inputs` row. */
function inputStringField<T extends "score_target" | "subscale">(ev: EntryValueViewT, key: T): string | null {
    const direct = ev[key];
    if (typeof direct === "string" && direct !== "") {
        return direct;
    }
    const inputs = (ev as unknown as { inputs?: Partial<Record<T, string | null>> }).inputs;
    const nested = inputs && typeof inputs === "object" ? inputs[key] : null;
    return typeof nested === "string" && nested !== "" ? nested : null;
}

/** Hard-coded scale ceilings for known input types where the range is fixed by design. */
const KNOWN_INPUT_CEILINGS: Partial<Record<string, { min: number; max: number }>> = {
    "pain-slider": { min: 0, max: 10 },
    "glucose-slider": { min: 0, max: 20 },
    "ratings-list": { min: 0, max: 10 },
    "ms-adl-ratings-list": { min: 0, max: 10 },
    "fixed-ratings-list": { min: 0, max: 10 },
};

/** Free-text (PSFS) and catalogue (MS-ADL) activity rating lists store `{ activity: score }` in json_value. */
const RATINGS_LIST_INPUT_KEYS = new Set(["ratings-list", "ms-adl-ratings-list", "fixed-ratings-list"]);

function isRangeBoundedInput(ev: EntryValueViewT): boolean {
    return [
        "pain-slider",
        "slider",
        "glucose-slider",
        "scale-rating",
        "ratings-list",
        "ms-adl-ratings-list",
        "fixed-ratings-list",
    ].includes(ev.input_key ?? "");
}

/** Best-effort scale ceiling for charted inputs, preferring slider/rating range over stale score metadata. */
export function chartInputCeiling(ev: EntryValueViewT): number | null {
    const maxValue = inputValue(ev, "max_value");
    const maxScore = inputValue(ev, "max_score");

    // Range inputs: prefer explicit max_value, then max_score, then hard-coded known ceiling.
    if (isRangeBoundedInput(ev)) {
        return maxValue ?? maxScore ?? KNOWN_INPUT_CEILINGS[ev.input_key ?? ""]?.max ?? null;
    }

    return maxScore ?? maxValue;
}

/** Best-effort scale floor for charted inputs. */
export function chartInputFloor(ev: EntryValueViewT): number | null {
    const minValue = inputValue(ev, "min_value");
    if (isRangeBoundedInput(ev)) {
        return minValue ?? KNOWN_INPUT_CEILINGS[ev.input_key ?? ""]?.min ?? null;
    }
    return minValue;
}

function pickEntryValueMinMax(ev: EntryValueViewT): { min: number | null; max: number | null } {
    if (isRangeBoundedInput(ev)) {
        return {
            min: chartInputFloor(ev),
            max: chartInputCeiling(ev),
        };
    }

    const known = KNOWN_INPUT_CEILINGS[ev.input_key ?? ""];
    return {
        min: inputValue(ev, "min_value") ?? known?.min ?? null,
        max: inputValue(ev, "max_value") ?? known?.max ?? null,
    };
}

/**
 * Y-axis bounds from entry rows (top-level or nested `inputs`). Optionally only rows with chart_order.
 */
export function getMinMaxBoundsFromEntryValues(
    entries: EntryViewT[],
    formId: string,
    opts?: { chartOrderOnly?: boolean }
): { min: number | null; max: number | null } {
    const chartOrderOnly = opts?.chartOrderOnly ?? false;
    // Some payload shapes (tracker entries_view) omit form_id on entry_values rows,
    // so treat null/undefined the same as a match – consistent with filterEntryByFormId.
    const formEntries = entries.filter((e) =>
        e.entry_values?.some((ev) => ev?.form_id === formId || ev?.form_id == null)
    );
    const hasRatingsList = hasRatingsListValues(formEntries);
    // Free-text / locked activity JSON series take priority over chart_order on aggregate scores
    // (e.g. PSFS Average Score wrongly tagged chart_order=1 would otherwise hide activities).
    const hasMeta = !hasRatingsList && formEntries.some((e) => e.entry_values?.some((ev) => ev?.chart_order != null));
    const chartUsesRatingsList = hasRatingsList;
    let computedMin: number | null = null;
    let computedMax: number | null = null;

    for (const entry of formEntries) {
        for (const ev of entry.entry_values ?? []) {
            if (ev.form_id != null && ev.form_id !== formId) {
                continue;
            }
            if (chartOrderOnly && ev.chart_order == null) {
                continue;
            }
            if (chartUsesRatingsList && !RATINGS_LIST_INPUT_KEYS.has(ev.input_key ?? "")) {
                continue;
            }

            const { min: rowMin, max: rowMax } = pickEntryValueMinMax(ev);
            if (rowMin != null) {
                computedMin = computedMin === null ? rowMin : Math.min(computedMin, rowMin);
            }
            if (rowMax != null) {
                computedMax = computedMax === null ? rowMax : Math.max(computedMax, rowMax);
            }
        }
    }

    return { min: computedMin, max: computedMax };
}

/** Returns true if any entry value in the dataset has chart_order metadata set. */
export function hasChartOrderMetadata(entries: EntryViewT[]): boolean {
    return entries.some((e) => e.entry_values?.some((ev) => ev?.chart_order != null));
}

/**
 * Returns ordered chart series (chart_order != null) for the given form,
 * sorted ascending by chart_order.
 */
export function getChartSeriesMetadata(entries: EntryViewT[], formId: string): ChartSeriesMeta[] {
    const seriesMap = new Map<
        string,
        { chartOrder: number; maxScore: number | null; isPrimary: boolean; scoreTarget: string | null }
    >();
    const formEntries = filterEntryByFormId(entries, formId);

    for (const entry of formEntries) {
        for (const ev of entry.entry_values ?? []) {
            if (ev.chart_order == null) {
                continue;
            }
            const ceiling = chartInputCeiling(ev);
            const label = ev.input_label || "value";
            const existing = seriesMap.get(label);
            if (!existing) {
                seriesMap.set(label, {
                    chartOrder: ev.chart_order,
                    maxScore: ceiling,
                    isPrimary: ev.is_primary_score ?? false,
                    scoreTarget: inputStringField(ev, "score_target"),
                });
            } else {
                if (ceiling != null) {
                    existing.maxScore = existing.maxScore == null ? ceiling : Math.max(existing.maxScore, ceiling);
                }
                existing.scoreTarget ??= inputStringField(ev, "score_target");
            }
        }
    }

    return [...seriesMap.entries()]
        .toSorted((a, b) => a[1].chartOrder - b[1].chartOrder)
        .map(([label, meta], i) => ({
            label,
            chartOrder: meta.chartOrder,
            maxScore: meta.maxScore,
            isPrimary: meta.isPrimary,
            scoreTarget: meta.scoreTarget,
            color: CHART_COLORS[i % CHART_COLORS.length],
        }));
}

/**
 * Returns the label and max_score of the first non-charted primary score
 * (is_primary_score=true with chart_order=null), or null if none.
 */
export function getNonChartedPrimaryMeta(
    entries: EntryViewT[],
    formId: string
): { label: string; maxScore: number | null } | null {
    const formEntries = filterEntryByFormId(entries, formId);
    for (const entry of formEntries) {
        for (const ev of entry.entry_values ?? []) {
            if (ev.is_primary_score && ev.chart_order == null) {
                return { label: ev.input_label, maxScore: chartInputCeiling(ev) };
            }
        }
    }
    return null;
}

// FAAM specific input IDs that need preprocessing
const FAAM_INPUT_IDS = [UUIDs.faamInputs.adlSubscale, UUIDs.faamInputs.sportsSubscale, UUIDs.faamInputs.totalScore];

// EQ-5D specific input IDs that need preprocessing
const EQ5D_TOTAL_INPUT_ID = UUIDs.eq5dInputs.eq5dTotal;

/**
 * Preprocesses FAAM entries to convert text values like '80/84' into numeric values
 * while preserving the original text for tooltip display
 */
export function preprocessFaamEntries(entries: EntryViewT[]): EntryViewT[] {
    return entries.map((entry) => {
        const processedEntryValues = entry.entry_values?.map((entryValue) => {
            // Only process FAAM input IDs
            if (!FAAM_INPUT_IDS.includes(entryValue.input_id as (typeof FAAM_INPUT_IDS)[number])) {
                return entryValue;
            }

            // Parse the text_value (e.g., '80/84' -> 80)
            const textValue = entryValue.text_value;
            if (typeof textValue === "string" && textValue.includes("/")) {
                const [numerator, denominator] = textValue.split("/");
                const numericNumerator = Number(numerator);
                const numericDenominator = Number(denominator);

                if (!Number.isNaN(numericNumerator) && !Number.isNaN(numericDenominator) && numericDenominator > 0) {
                    const percentage = (numericNumerator / numericDenominator) * 100;
                    return {
                        ...entryValue,
                        number_value: percentage,
                        input_key: "number", // Change to number so existing functions work
                        // Keep original text_value for tooltip
                    };
                }
            }

            return entryValue;
        });

        return {
            ...entry,
            entry_values: processedEntryValues,
        } as EntryViewT;
    });
}

/**
 * Preprocesses EQ-5D entries to make the Index Score chartable.
 * The EQ-5D-5L Index Score is a calculated value (stored as number_value) but the input
 * has input_key "short-text" in the database. We normalize to input_key "number" and
 * input_label "EQ-5D-5L Index Score" so convertEntriesToChartData includes it.
 */
export function preprocessEq5dEntries(entries: EntryViewT[]): EntryViewT[] {
    return entries.map((entry) => {
        const processedEntryValues = entry.entry_values?.map((entryValue) => {
            // Check if this is the EQ-5D Index Score input by ID or label
            const isEq5dIndexScore =
                entryValue.input_id === EQ5D_TOTAL_INPUT_ID ||
                entryValue.input_label === "EQ-5D-5L Total Score" ||
                entryValue.input_label === "EQ-5D-5L Index Score";

            if (!isEq5dIndexScore) {
                return entryValue;
            }

            // Normalize so the chart picks it up: use input_key "number" (value is in number_value)
            return {
                ...entryValue,
                input_key: "number",
                input_label: "EQ-5D-5L Index Score",
            };
        });

        return {
            ...entry,
            entry_values: processedEntryValues,
        } as EntryViewT;
    });
}

export function isEntryValueViewT(value: unknown): value is EntryValueViewT {
    if (!value || typeof value !== "object") return false;
    const obj = value as Record<string, unknown>;
    return "input_key" in obj && "input_label" in obj && "number_value" in obj;
}

interface ChartEntry {
    date: string;
    timing?: string;
    timing_label?: string;
    /** Number of patients contributing to this aggregated time point, when known. */
    patients?: number;
    [key: string]: string | number | undefined;
}

const CHART_POINT_META_KEYS = new Set(["date", "timing", "timing_label", "patients"]);

function overlayTimeKey(point: ChartEntry): string {
    const timing = point.timing_label || point.timing;
    if (typeof timing === "string" && timing.trim()) return `t:${timing}`;
    return `d:${point.date ?? ""}`;
}

function seriesValuesConflict(left: ChartEntry, right: ChartEntry): boolean {
    for (const [key, value] of Object.entries(right)) {
        if (CHART_POINT_META_KEYS.has(key) || value == null || Number.isNaN(value)) continue;
        const existing = left[key];
        if (existing == null || Number.isNaN(existing)) continue;
        if (existing !== value) return true;
    }
    return false;
}

/**
 * Put series that share a time label on one point.
 * Split rows (local pain on one entry, secondary pain on the next, same timing)
 * otherwise plot as neighbouring categories, so the lines sit side by side.
 * Rows that disagree on a series stay separate.
 */
export function coalesceOverlayChartPoints(points: ChartEntry[]): ChartEntry[] {
    const result: ChartEntry[] = [];
    const openIndex = new Map<string, number>();

    for (const point of points) {
        const key = overlayTimeKey(point);
        const index = openIndex.get(key);
        if (index == null || seriesValuesConflict(result[index], point)) {
            openIndex.set(key, result.length);
            result.push({ ...point });
            continue;
        }

        const target = result[index];
        for (const [seriesKey, value] of Object.entries(point)) {
            if (CHART_POINT_META_KEYS.has(seriesKey) || value == null || Number.isNaN(value)) continue;
            if (target[seriesKey] == null || Number.isNaN(target[seriesKey])) {
                target[seriesKey] = value;
            }
        }
    }

    return result;
}

/** Scheduled due date from the tracker processed schedule, when available. */
export function scheduleDueAtForEntry(entry: EntryViewT, processedSchedule?: DueEntryT[] | null): string | null {
    if (entry.schedule_index == null || !processedSchedule?.length) {
        return null;
    }

    const slot = processedSchedule.find((s) => s != null && s.schedule_index === entry.schedule_index);
    return slot?.due_at ?? null;
}

/** Plot/sort date: prefer schedule slot due_at over calculated_entry_time (baseline can share the wrong calc time). */
export function entryChartDate(entry: EntryViewT, processedSchedule?: DueEntryT[] | null): string {
    return (
        scheduleDueAtForEntry(entry, processedSchedule) ??
        entry.calculated_entry_time ??
        entry.entry_time ??
        new Date().toISOString()
    );
}

/** Chronological order for scheduled entries; falls back to entry timestamps for extras. */
export function compareEntriesChronologically(
    a: EntryViewT,
    b: EntryViewT,
    processedSchedule?: DueEntryT[] | null
): number {
    const scheduleDueA = scheduleDueAtForEntry(a, processedSchedule);
    const scheduleDueB = scheduleDueAtForEntry(b, processedSchedule);

    if (scheduleDueA && scheduleDueB) {
        const cmp = new Date(scheduleDueA).getTime() - new Date(scheduleDueB).getTime();
        if (cmp !== 0) {
            return cmp;
        }
    }

    const indexA = a.schedule_index;
    const indexB = b.schedule_index;
    if (indexA != null && indexB != null && indexA !== indexB) {
        return indexA - indexB;
    }

    const dateA = new Date(scheduleDueA ?? a.calculated_entry_time ?? a.entry_time ?? 0).getTime();
    const dateB = new Date(scheduleDueB ?? b.calculated_entry_time ?? b.entry_time ?? 0).getTime();
    if (dateA !== dateB) {
        return dateA - dateB;
    }

    if (indexA != null && indexB != null) {
        return indexA - indexB;
    }

    return String(a.entry_id ?? "").localeCompare(String(b.entry_id ?? ""));
}

/** Legacy diary chart inputs when a form has no `chart_order` metadata (see `convertEntriesToChartData`). */
export const CHARTABLE_LEGACY_INPUT_KEYS = ["number", "pain-slider", "slider", "single-choice"] as const;

/** Expand ratings-list / MS-ADL json_value into per-activity numeric series for charts. */
export function ratingsListValues(ev: EntryValueViewT): Record<string, number> | null {
    if (!RATINGS_LIST_INPUT_KEYS.has(ev.input_key ?? "")) {
        return null;
    }

    const jsonValue = ev.json_value;
    if (!jsonValue || typeof jsonValue !== "object" || Array.isArray(jsonValue)) {
        return null;
    }

    const values = Object.entries(jsonValue).reduce<Record<string, number>>((acc, [activity, rawValue]) => {
        const value = typeof rawValue === "number" ? rawValue : Number(rawValue);
        if (activity && Number.isFinite(value)) {
            acc[activity] = value;
        }
        return acc;
    }, {});

    return Object.keys(values).length > 0 ? values : null;
}

export function hasRatingsListValues(entries: EntryViewT[]): boolean {
    return entries.some((entry) => entry.entry_values?.some((ev) => ratingsListValues(ev) != null));
}

/**
 * Entry values on the same form row as `ev`, matching how charts associate values with a form
 * (`form_id` match or nullish form_id on sibling rows).
 */
function chartSiblingsForEntryValue(ev: EntryValueViewT, allEntryValues: EntryValueViewT[]): EntryValueViewT[] {
    const fid = ev.form_id;
    if (fid != null && fid !== "") {
        return allEntryValues.filter(
            (v) => v.form_id === fid || v.form_id == null || v.form_id === undefined || v.form_id === ""
        );
    }
    return allEntryValues;
}

/**
 * Whether this value is surfaced as a headline / chart-style metric in history, aligned with
 * `convertEntriesToChartData`: if any sibling has `chart_order`, use `chart_order` or `is_primary_score`
 * (from form input definitions); otherwise legacy chartable input keys.
 */
export function isEntryValueChartAligned(ev: EntryValueViewT, allEntryValues: EntryValueViewT[]): boolean {
    const siblings = chartSiblingsForEntryValue(ev, allEntryValues);
    const hasChartOrderMeta = siblings.some((s) => s.chart_order != null);
    if (hasChartOrderMeta) {
        return ev.chart_order != null || ev.is_primary_score;
    }
    return CHARTABLE_LEGACY_INPUT_KEYS.includes(ev.input_key as (typeof CHARTABLE_LEGACY_INPUT_KEYS)[number]);
}

function filterEntryByFormId(entries: EntryViewT[], formId: string) {
    return entries.filter((e) =>
        e.entry_values?.some(
            (ev: EntryValueViewT) =>
                // Match by form_id if available, or include entries with null form_id
                // (for computed scores like VISA-P Score that don't have a page_id)
                ev?.form_id === formId || ev?.form_id === null || ev?.form_id === undefined
        )
    );
}

export function convertEntriesToChartConfig(entries: EntryViewT[], formId: string): ChartConfig {
    const chartConfig: ChartConfig = {};
    const formEntries = filterEntryByFormId(entries, formId);

    // When chart_order metadata is present, filter to those inputs and sort by chart_order
    const hasRatingsList = hasRatingsListValues(formEntries);
    const hasMeta = !hasRatingsList && formEntries.some((e) => e.entry_values?.some((ev) => ev?.chart_order != null));
    const ordered: { label: string; order: number }[] = [];
    const seen = new Set<string>();

    for (const formEntry of formEntries) {
        for (const value of formEntry.entry_values ?? []) {
            if (hasRatingsList) {
                const activityRatings = ratingsListValues(value);
                if (!activityRatings) {
                    continue;
                }
                for (const activity of Object.keys(activityRatings)) {
                    if (!seen.has(activity)) {
                        seen.add(activity);
                        ordered.push({ label: activity, order: ordered.length + 1 });
                    }
                }
                continue;
            }

            const isChartable = hasMeta
                ? value.chart_order != null
                : CHARTABLE_LEGACY_INPUT_KEYS.includes(value.input_key as (typeof CHARTABLE_LEGACY_INPUT_KEYS)[number]);

            if (isChartable) {
                const label = value.input_label || "value";
                if (!seen.has(label)) {
                    seen.add(label);
                    ordered.push({ label, order: value.chart_order ?? 999 });
                }
            }
        }
    }

    if (hasMeta) {
        ordered.sort((a, b) => a.order - b.order);
    }

    ordered.forEach(({ label }, i) => {
        chartConfig[label] = {
            label,
            color: CHART_COLORS[i % CHART_COLORS.length],
        };
    });

    return chartConfig;
}

/**
 * X-axis timing label for a chart point.
 * Autoname schedule slots often persist blank `timing_label` while keeping interval/unit
 * (and/or a generated label on the processed schedule slot).
 */
export function resolveEntryChartTimingLabel(
    entry: Pick<EntryViewT, "timing_label" | "timing_interval" | "time_unit" | "schedule_index">,
    processedSchedule?: DueEntryT[] | null
): string | undefined {
    const scheduleSlot =
        entry.schedule_index != null && processedSchedule?.length
            ? processedSchedule.find((slot) => slot != null && slot.schedule_index === entry.schedule_index)
            : null;

    return (
        resolveScheduleStepLabel({
            timing_label: entry.timing_label,
            label: scheduleSlot?.label,
            timing_interval: entry.timing_interval ?? scheduleSlot?.interval,
            time_unit: entry.time_unit ?? scheduleSlot?.time_unit,
            schedule_index: entry.schedule_index ?? scheduleSlot?.schedule_index,
        }) ?? undefined
    );
}

export function convertEntriesToChartData(
    entries: EntryViewT[],
    formId: string,
    processedSchedule?: DueEntryT[] | null
): ChartEntry[] {
    const entriesFromForm = entries.filter((e) =>
        e.entry_values?.some(
            (ev: EntryValueViewT) => ev?.form_id === formId || ev?.form_id === null || ev?.form_id === undefined
        )
    );

    const sortedEntries = [...entriesFromForm].toSorted((a, b) =>
        compareEntriesChronologically(a, b, processedSchedule)
    );

    // When chart_order metadata is present:
    // - include values with chart_order != null (plotted series)
    // - also include is_primary_score values without chart_order (non-plotted summary, accessible in data panel)
    // Ratings-list JSON (PSFS / MS-ADL) always wins so aggregate chart_order rows cannot hide activities.
    const hasRatingsList = hasRatingsListValues(entriesFromForm);
    const hasMeta =
        !hasRatingsList && entriesFromForm.some((e) => e.entry_values?.some((ev) => ev?.chart_order != null));

    const points = sortedEntries.map((entry) => {
        const entryDate = entryChartDate(entry, processedSchedule);
        const patientCount = (entry as unknown as { patient_count?: number }).patient_count;
        const timingLabel = resolveEntryChartTimingLabel(entry, processedSchedule);
        const formattedEntry: ChartEntry = {
            date: entryDate,
            timing: timingLabel,
            timing_label: timingLabel,
            ...(typeof patientCount === "number" ? { patients: patientCount } : {}),
        };

        const formEntryValues = entry.entry_values?.filter((ev: EntryValueViewT) => {
            if (hasRatingsList) {
                return ratingsListValues(ev) != null;
            }
            if (hasMeta) {
                return ev.chart_order != null || ev.is_primary_score;
            }
            return CHARTABLE_LEGACY_INPUT_KEYS.includes(ev.input_key as (typeof CHARTABLE_LEGACY_INPUT_KEYS)[number]);
        });

        for (const ev of formEntryValues ?? []) {
            const activityRatings = ratingsListValues(ev);
            if (activityRatings) {
                for (const [activity, rating] of Object.entries(activityRatings)) {
                    formattedEntry[activity] = rating;
                }
                continue;
            }

            const label = ev.input_label ?? "value";
            if (ev.input_key === "single-choice") {
                const textValue = ev.text_value;
                formattedEntry[label] = typeof textValue === "string" ? textValue : String(textValue ?? "");
            } else if (ev.input_key === "yes-no") {
                // Map Yes/No text answers to 1/0 for chart rendering
                const tv = ev.text_value?.toLowerCase();
                formattedEntry[label] = tv === "yes" ? 1 : tv === "no" ? 0 : undefined;
            } else {
                formattedEntry[label] = Number(ev.number_value);
            }
        }

        return formattedEntry;
    });

    return coalesceOverlayChartPoints(points);
}
